import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CButton, CCol, CForm, CRow, CLink } from '@coreui/react';
import { Formik, FieldArray } from 'formik';
import CIcon from '@coreui/icons-react';
import { AIInsightsDetailContext } from '../Popup/AIInsightsTemplate';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import StepCardHeader from '../Components/StepCardHeaders'
import Select from "react-select";
import { CInputCheckbox } from '../../../../../migration/CInput';
import { HoverInfoIconTooltip } from '../../../../../general/custom-tooltips';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { API_AI_INSIGHTS_REPORT_MULTIPLE, API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE, API_CLIENT_DIMENSIONS, API_CLIENT_GET_LIST_SEGMENT, API_CLIENT_GET_MAX_STEP_FORM_CATEGORY, API_CLIENT_SALES_CONV_NAME, API_CLIENT_SECONDARY_OBJECT, API_GET_ECO_ITEM_CATEGORIES, CLIENT_GET_SAVED_REPORT_BY_ID, CLIENT_REPORT_VIEW, COMPONENT_NAME, DEFAULT_DATE_FORMAT, REPORT_DATA_TYPES, REPORT_NAMES } from '../../../../../../constants';
import { setFlexibleMultiModal } from '../../../../../../actions/common';
import { callCompressTokenApi, callTokenApi } from '../../../../../../apiCaller';
import { NoOptionMenuList, NoOptionsMessage } from '../../../../../general/rules';
import SelectSearchField from '../../../../../general/custom-search-select/SelectSearchField';
import FormikStateObserver from '../Components/FormikStateObserver';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import Waiting from '../../../../../general/Loadings/Waiting';
import { camelCase, formatDate, handleAddMetricSalesConversion, handleAddMetricSalesConvNameIndividual } from '../../../../../../utils';
import { fetchSavedReportsRequest } from '../../../../../../actions/subscriber';
import { fetchAiInsightAvailableReports, buildReportOptionLabel } from '../../../../../../utils/aiInsightDataSources';

const handleGenerationMetricsStepForm = (number) => {
    const metricsStepForm = [];
    for (let i = 1; i <= number; i++) {
        metricsStepForm.push(
            { key: `step${i}View`, label: `Step ${i} - View` },
            { key: `step${i}Start`, label: `Step ${i} - Start` },
            { key: `step${i}Complete`, label: `Step ${i} - Complete` }
        );
    }
    return metricsStepForm;
};

const DataSources = () => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        handleSetUnsavedChanges,
        clearUnsavedChangesFlag,
        setReFreshTemplate,
    } = useContext(AIInsightsDetailContext);
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [loadingIndex, setLoadingIndex] = useState(null);
    const [itemDataSource, setItemDataSource] = useState(stepsData?.dataSources || [
        {
            id: Date.now(),
            reportViewId: '',
            blockIfEmpty: false,
            isNew: true
        },
    ]);
    const [availableReports, setAvailableReports] = useState([]);
    const dispatch = useDispatch();
    const activeAccount = useSelector((state) => state.subscriber.activeAccount);
    const user = useSelector(state => state.theme.user);
    const customDimensions = useSelector((state) => state.subscriber.customReport.dimensions);
    const segmentCacheRef = useRef({});
    const dimensionCacheRef = useRef({});

    const handleEditField = (reportViewId, descriptionOverride, setFieldValue, index) => {
        if (!reportViewId) return;

        // The source report's raw name/description come from the same fetched
        // list Step 2 already holds, not the active-account-only redux
        // `savedReport` slice, so cross-account rows resolve correctly too.
        const originalReportData = availableReports.find((report) => report.id === reportViewId);

        dispatch(
            setFlexibleMultiModal({
                show: true,
                component: COMPONENT_NAME.AI_INSIGHTS_DATA_SOURCE_EDIT_FIELD,
                attributes: {
                    id: reportViewId,
                    descriptionOverride,
                    setFieldValue,
                    index,
                    originalReportData,
                }
            })
        );

        handleSetUnsavedChanges(false);
    };

    const onSubmit = useCallback(
        async values => {
            if (!Array.isArray(values.dataSources) || values.dataSources.length === 0) {
                toast.error('No data sources available');
                return;
            }

            const oldData = stepsData.dataSources || [];
            const newData = values.dataSources;

            const accountId = activeAccount.accountId;
            const templateId = stepsData.id;

            const formattedNew = newData.map(item => ({
                id: item.id,
                accountId: item.accountId || accountId,
                templateId,
                reportViewId: item.reportViewId,
                blockIfEmpty: item.blockIfEmpty,
                isNew: item.isNew || false,
                descriptionOverride: item.descriptionOverride,
            }));

            const oldMap = new Map(oldData.map(item => [item.id, item]));

            const toCreate = [];
            const toUpdate = [];
            const toDelete = [];

            for (const item of formattedNew) {
                if (item.isNew) {
                    toCreate.push(item);
                } else {
                    if (item.id) {
                        toUpdate.push(item);
                    }

                    oldMap.delete(item.id);
                }
            }

            toDelete.push(...Array.from(oldMap.keys()));

            try {
                const sanitized = item => {
                    // eslint-disable-next-line no-unused-vars
                    const { isNew, accountName, ...rest } = item;
                    return rest;
                };

                const response = await callTokenApi(API_AI_INSIGHTS_REPORT_MULTIPLE, 'POST', {
                    create: toCreate.map(sanitized),
                    update: toUpdate.map(sanitized),
                    delete: toDelete,
                });

                if (response?.data?.code === 200 || response?.data?.code === 201) {
                    const { errors: rowErrors } = response?.data || {};
                    const hasRowErrors = Array.isArray(rowErrors) && rowErrors.length > 0;

                    if (hasRowErrors) {
                        const [firstError, ...remainingErrors] = rowErrors;
                        const baseMessage = firstError?.error || 'Some data sources could not be saved.';
                        const message = remainingErrors.length > 0
                            ? `${baseMessage} (+${remainingErrors.length} more error${remainingErrors.length > 1 ? 's' : ''})`
                            : baseMessage;
                        toast.error(message);
                    }

                    const { created = [], updated = [] } = response?.data?.data || {};
                    const merged = [...created, ...updated];
                    let updatedDataSources = Array.isArray(stepsData.dataSources)
                        ? [...stepsData.dataSources]
                        : [];

                    if (merged.length > 0) {
                        merged.forEach(item => {
                            const index = updatedDataSources.findIndex(existingItem => existingItem.id === item.id);
                            if (index >= 0) {
                                updatedDataSources[index] = item;
                            } else {
                                updatedDataSources.push(item);
                            }
                        });
                    }

                    if (toDelete.length > 0) {
                        updatedDataSources = updatedDataSources.filter(item => !toDelete.includes(item.id));
                    }

                    const currentStep = 2;

                    const responseTemplate = await callTokenApi(
                        `${API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE}/${stepsData.id}`,
                        'PUT',
                        {
                            accountId: activeAccount.accountId,
                            lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > currentStep
                                ? stepsData.lastEditStep
                                : currentStep,
                        }
                    );

                    setItemDataSource(updatedDataSources);
                    setStepsData({
                        ...stepsData,
                        lastEditStep: responseTemplate?.data?.data.lastEditStep,
                        dataSources: updatedDataSources,
                    });

                    if (!hasRowErrors) {
                        toast.success('Data Sources updated successfully.');
                        setActiveStep(3);
                    }

                    //fix #65770
                    dispatch(fetchSavedReportsRequest(accountId, user.id));

                    if(setReFreshTemplate) setReFreshTemplate(true);
                } else {
                    toast.error('Failed to update data sources. Please try again.');
                }
            } catch (error) {
                console.error('Submit error:', error);
                toast.error('Something went wrong while saving. Please try again.');
            } finally {
                if (typeof clearUnsavedChangesFlag === 'function') {
                    clearUnsavedChangesFlag(); // Clear flag only on FULL success
                }
            }
        },
        [stepsData, setStepsData, setActiveStep, clearUnsavedChangesFlag, activeAccount.accountId, user.id, dispatch, setReFreshTemplate]
    );

    const validationSchema = Yup.object().shape({
        dataSources: Yup.array()
            .of(
                Yup.object().shape({
                    reportViewId: Yup.string().required('Please select a report'),
                    blockIfEmpty: Yup.boolean().nullable(),
                })
            )
            .test('unique-reportViewId', 'Each report must be unique', (dataSources) => {
                const ids = dataSources?.map((item) => item.reportViewId);
                return new Set(ids).size === ids.length;
            }),
    });

    const fetchSalesConversionName = async (accountId) => {
        let salesConversionNameList = [];

        const response = await callTokenApi(API_CLIENT_SALES_CONV_NAME.replace(':accountId', accountId), 'GET', null);

        if (response.status === 200) {
            const { conversionName } = response.data;
            salesConversionNameList = conversionName;
        }

        return salesConversionNameList;
    }

    const fetchSalesConversionObject = async (accountId) => {
        let primaryObjectList = [];
        let secondaryObjectList = [];

        const response = await callTokenApi(API_CLIENT_SECONDARY_OBJECT.replace(':accountId', accountId), 'GET', null);

        if (response.status === 200) {
            const { primaryConversion, secondaryObjects } = response.data;

            primaryObjectList = primaryConversion;
            secondaryObjectList = secondaryObjects;
        }

        return {
            primaryObjectList,
            secondaryObjectList,
        };
    }

    // Segments are account-scoped. Cache per account so a cross-account report
    // resolves its own account's segments instead of the active account's.
    const fetchSegment = useCallback(async (accountId) => {
        if (!accountId) return [];
        if (segmentCacheRef.current[accountId]) return segmentCacheRef.current[accountId];

        try {
            const response = await callTokenApi(API_CLIENT_GET_LIST_SEGMENT.replace(':accountId', accountId), 'GET', null);
            const segments = response?.data?.segments || [];
            segmentCacheRef.current[accountId] = segments;
            return segments;
        } catch (error) {
            return [];
        }
    }, []);

    // Custom dimensions are account-scoped too. The active account's list is
    // already in redux (`customDimensions`); any other account is fetched and
    // cached the same way as segments.
    const fetchCustomDimensions = useCallback(async (accountId) => {
        if (!accountId) return [];
        if (accountId === activeAccount.accountId) return customDimensions;
        if (dimensionCacheRef.current[accountId]) return dimensionCacheRef.current[accountId];

        try {
            const response = await callTokenApi(API_CLIENT_DIMENSIONS.replace(':accountId', accountId), 'GET', null);
            const dimensions = response?.status === 200 ? (response.data?.dimensions || []) : [];
            dimensionCacheRef.current[accountId] = dimensions;
            return dimensions;
        } catch (error) {
            return [];
        }
    }, [activeAccount.accountId, customDimensions]);

    const camelCaseToSentence = (str) => {
        return `${camelCase(
            str
                .replace(/\b\w/g, (match) => match.toUpperCase())
                .replace(/^\w/, (match) => match.toLowerCase())
                .replace(/\s+/g, '')
        )}Custom`
    }

    useEffect(() => {
        if (!activeAccount?.accountId || !stepsData?.id || activeStep !== 2) return;

        let cancelled = false;

        setIsLoadingReports(true);
        fetchAiInsightAvailableReports({
            templateId: stepsData.id,
            accountId: activeAccount.accountId,
            userId: user.id,
            onError: () => {
                if (cancelled) return;
                toast.error('Failed to load available reports. Please try again.');
            },
        }).then((reports) => {
            if (cancelled) return;
            setAvailableReports(reports);
            setIsLoadingReports(false);
        });

        return () => { cancelled = true; };
    }, [activeAccount?.accountId, stepsData?.id, activeStep, user.id]);

    const fetchEcommerceItems = async (reportName, sourceAccountId) => {
        let eComItems = [];

        if (reportName === REPORT_NAMES.ECOMMERCE_ITEMS) {
            const response = await callTokenApi(API_GET_ECO_ITEM_CATEGORIES, 'POST', { accountId: sourceAccountId });

            if (response?.status === 200) {
                const itemCategories = response.data.itemCategories;
                eComItems = itemCategories;
            } else {
                eComItems = []
            }
        }

        return eComItems;
    }

    const fetchFormCategoryReport = async (reportName, sourceAccountId) => {
        let metricsStepForm = [];

        if (reportName === REPORT_NAMES.FORM_CATEGORY_REPORT) {
            const response = await callTokenApi(API_CLIENT_GET_MAX_STEP_FORM_CATEGORY, 'POST', {
                reportName,
                accountId: sourceAccountId,
                dateRanges: {
                    selection: {
                        startDate: '2020-01-01',
                        endDate: formatDate(new Date(), DEFAULT_DATE_FORMAT),
                    }
                }
            });

            if (response?.status === 200) {
                metricsStepForm = handleGenerationMetricsStepForm(response.data.maxStepForm);
            } else {
                metricsStepForm = [];
            }
        }

        return metricsStepForm;
    }

    const getReportFilter = async (reportViews = {}, sourceAccountId) => {
        const { reportName, scoped, content, payloadReportFilter } = reportViews;
        const parseReportFilter = typeof payloadReportFilter === 'string' ? JSON.parse(payloadReportFilter) : payloadReportFilter;

        const reportDataName = REPORT_DATA_TYPES?.[reportName];
        const scopedData = reportDataName?.[scoped];

        const reportDataTypes = !scopedData || typeof scopedData === 'string'
            ? reportDataName
            : scopedData;

        let resultFilters;

        if (parseReportFilter) {
            resultFilters = parseReportFilter;
        } else {
            let dimensions = reportDataTypes?.dimensions || [];
            let metrics = reportDataTypes?.metrics || [];
            const properties = reportDataTypes?.properties || [];
            const weights = reportDataTypes?.weights || [];

            if (reportName === REPORT_NAMES.ECOMMERCE_ITEMS) {
                const eCoItemCategories = await fetchEcommerceItems(reportName, sourceAccountId);
                dimensions = [...dimensions, ...eCoItemCategories];
            }

            if (reportName === REPORT_NAMES.FORM_CATEGORY_REPORT) {
                const metricsStepForm = await fetchFormCategoryReport(reportName, sourceAccountId);
                metrics = [...metrics, ...metricsStepForm];
            }

            function updateMetrics(metricsSource, filterKey) {
                if (metricsSource) {
                    metrics = metrics.filter(metric => !metric[filterKey]);
                
                    metricsSource.forEach(item => {
                        if (!metrics.some(metric => metric.key === item.key)) {
                            metrics.push(item);
                        }
                    });
                }
            }

            if (reportName === REPORT_NAMES.SALES_CONVERSION_BY_CONVERSION || reportName === REPORT_NAMES.SALES_CONVERSION_BY_PROPERTY) {
                const salesConversionNameList = await fetchSalesConversionName(sourceAccountId);
                const { secondaryObjectList } = await fetchSalesConversionObject(sourceAccountId);
                const metricSecondaryObject = handleAddMetricSalesConversion(secondaryObjectList, reportName);
                const metricIndividualSalesConversion = handleAddMetricSalesConvNameIndividual(salesConversionNameList, reportName);

                updateMetrics(metricSecondaryObject, 'object');
                updateMetrics(metricIndividualSalesConversion, 'conversionID');
            }

            const customFilterDimensions = (await fetchCustomDimensions(sourceAccountId)).filter((item) => item.status);
            const customPropertiesMapping = customFilterDimensions
                .filter((item) => content.properties.includes(camelCaseToSentence(item.name)))
                .map((item) => ({
                    key: camelCaseToSentence(item.name),
                    label: item.name,
                    description: item.description,
                }));

            const customDimensionsMapping = customFilterDimensions
                .filter((item) => content.dimensions.includes(camelCaseToSentence(item.name)))
                .map((item) => ({
                    key: camelCaseToSentence(item.name),
                    label: item.name,
                    description: item.description,
                }));

            const metricsProperties = [...properties, ...customPropertiesMapping, ...metrics];
            const filteredMetrics = metricsProperties.filter(item => content.metrics.includes(item.key));
            const filteredProperties = metricsProperties.filter(item => content.properties.includes(item.key));
            const filteredDimensions = [...customDimensionsMapping, ...dimensions].filter(item => content.dimensions.includes(item.key));
            const filteredWeight = weights.find(item => content.weight?.value === item.value);

            resultFilters = {
                filterMetric: filteredMetrics,
                filterProperties: filteredProperties,
                filterDimensions: filteredDimensions,
                weight: filteredWeight,
            };

            // This PUT body is deliberately incomplete: it omits accountId, exactly as it
            // does on master, so the server rejects it with a 400 and the write never
            // persists. Do NOT add accountId here. Once payloadReportFilter is non-null
            // for a report, getReportFilter starts serving that frozen snapshot instead of
            // recomputing from live custom dimensions, segments and sales-conversions, for
            // every account, forever. Activating that caching path is a pending product
            // decision, not a bug to be "fixed" - leaving the call failing keeps existing
            // (non cross-account) rules behaving exactly as they do today.
            await callCompressTokenApi(CLIENT_REPORT_VIEW, 'PUT', {
                id: reportViews.id,
                payloadReportFilter: JSON.stringify(resultFilters),
            });
        }

        const {
            filterMetric = [],
            filterProperties = [],
            filterDimensions = [],
            weight = null,
        } = resultFilters;

        return {
            reportModels: weight ? [{
                modelName: weight?.label,
                modelDescription: weight?.description || '',
                modelDefaultDescription: weight?.description || '',
                isModelCustomize: false,
            }] : [],
            reportDimensions: filterDimensions.map((dimension) => ({
                dimensionName: dimension?.label,
                dimensionDescription: dimension?.description || '',
                dimensionDefaultDescription: dimension?.description || '',
                isDimensionCustomize: false,
            })),
            reportMetrics: [...filterProperties, ...filterMetric]
                .filter((metric) => !['Profile', 'View in Salesforce'].includes(metric?.label))
                .map((metric) => ({
                    metricName: metric?.label,
                    metricDescription: metric?.description || '',
                    metricDefaultDescription: metric?.description || '',
                    isMetricCustomize: false,
                })),
        };
    }

    const handleSelectReportView = async ({ setFieldValue, index, value }) => {
        const rowSavedReports = availableReports.find((report) => report.id === value);
        if (!rowSavedReports) return;

        const sourceAccountId = rowSavedReports.accountId || activeAccount.accountId;

        setFieldValue(`dataSources.${index}.accountId`, sourceAccountId);
        setFieldValue(`dataSources.${index}.accountName`, rowSavedReports.accountName || '');

        if (rowSavedReports.viewId) {
            setLoadingIndex(index);

            const response = await callTokenApi(
                `${CLIENT_GET_SAVED_REPORT_BY_ID}/?id=${rowSavedReports.viewId}`,
                'GET'
            );

            if (response.status === 200) {
                const segments = await fetchSegment(sourceAccountId);
                let reportSegment = [];
                if (rowSavedReports.content?.segment && segments.length > 0) {
                    const matchedSegments = segments.filter((segment) =>
                        rowSavedReports.content.segment.includes(segment.id)
                    );

                    reportSegment = matchedSegments.map((segment) => ({
                        segmentName: segment.name,
                        segmentDescription: segment.description,
                        segmentDefaultDescription: segment.description,
                        isSegmentCustomize: false,
                    }));
                }

                const fullPayload = {
                    reportName: rowSavedReports.name || '',
                    isReportNameCustomize: false,
                    reportDescription: rowSavedReports.description || '',
                    isReportDescriptionCustomize: false,
                    reportSegment,
                    ...(await getReportFilter(response.data.reportViews, sourceAccountId))
                };

                setFieldValue(`dataSources.${index}.descriptionOverride`, JSON.stringify(fullPayload));
            }

            setFieldValue(
                `dataSources.${index}.reportViewId`,
                value
            );

            setLoadingIndex(null);
        }
    }

    return (
        <StepCardHeader
            title={`Data Sources`}
            step={2}
            activeStep={activeStep}
            lastEditStep={stepsData.lastEditStep}
            setActiveStep={() => setActiveStep(2)}
        >
            <p>Select the data that will be available for the analysis. Only saved reports with rolling date ranges are available for analysis. The rolling date range on the report should logically align to the schedule you will create for Insight generation. For example, you might run this analysis each month on the 3rd day of the month, and the saved reports you make available to it may include data from the previous month. </p>

            <Formik
                initialValues={{ dataSources: itemDataSource }}
                onSubmit={onSubmit}
                enableReinitialize
                validationSchema={validationSchema}
            >
                {({ values, errors, touched, dirty, setFieldValue, isSubmitting, handleSubmit }) => {
                    const selectedReportViewIds = values.dataSources.map((i) => i.reportViewId);

                    return (
                        <>
                            {isLoadingReports ? (
                                <div className='position-relative' style={{ padding: '40px 0' }}>
                                    <CenterSpinner />
                                </div>
                            ) : (
                                <>
                                    <FormikStateObserver dirty={dirty} onDirtyChange={handleSetUnsavedChanges} />

                                    <CForm onSubmit={handleSubmit} noValidate>
                                        <FieldArray
                                            name="dataSources"
                                            render={(arrayHelpers) => (
                                                <>
                                                    {values.dataSources.map((item, index) => {
                                                        const availableOptions = availableReports
                                                            .filter((report) =>
                                                                !selectedReportViewIds.includes(report.id) ||
                                                                report.id === item.reportViewId
                                                            ).map((r) => ({
                                                                value: r.id,
                                                                label: buildReportOptionLabel(r),
                                                            }));
                                                        return (
                                                            <CRow key={index} className="align-items-center mb-2">
                                                                <CCol lg="3">
                                                                    <CFormGroup className="mb-0">
                                                                        {(() => {
                                                                            const selectedValue =
                                                                                loadingIndex === index
                                                                                    ? undefined
                                                                                    : availableOptions.find(
                                                                                        (opt) => opt.value === item.reportViewId
                                                                                    );
                                                                            const isInvalid =
                                                                                errors?.dataSources?.[index]?.reportViewId &&
                                                                                touched?.dataSources?.[index]?.reportViewId;

                                                                            return availableOptions.length === 0 ? (
                                                                                <Select
                                                                                    classNamePrefix="react-select"
                                                                                    value={selectedValue}
                                                                                    isSearchable={false}
                                                                                    noOptionsMessage={() => (
                                                                                        <NoOptionsMessage
                                                                                            options={availableOptions}
                                                                                            showIcon={false}
                                                                                            noEmptyText="No reports available. "
                                                                                        />
                                                                                    )}
                                                                                    components={{ MenuList: NoOptionMenuList }}
                                                                                    className={`not-selected ${isInvalid ? 'is-invalid' : ''}`}
                                                                                    placeholder="Select a report"
                                                                                />
                                                                            ) : (
                                                                                <SelectSearchField
                                                                                    options={availableOptions}
                                                                                    value={selectedValue}
                                                                                    disabled={loadingIndex === index}
                                                                                    placeholder="Select a report"
                                                                                    onChange={(selected) =>
                                                                                        handleSelectReportView(
                                                                                            {
                                                                                                setFieldValue,
                                                                                                index,
                                                                                                value: selected?.value || '',
                                                                                            }
                                                                                        )
                                                                                    }
                                                                                />
                                                                            );
                                                                        })()}{' '}
                                                                        {loadingIndex === index && <CenterSpinner />}
                                                                        {errors?.dataSources?.[index]?.reportViewId &&
                                                                            touched?.dataSources?.[index]?.reportViewId && (
                                                                                <div className="text-danger small mt-1">
                                                                                    {errors.dataSources[index].reportViewId}
                                                                                </div>
                                                                            )}
                                                                    </CFormGroup>
                                                                </CCol>

                                                                <CCol lg="3">
                                                                    <div className="d-flex align-items-center">
                                                                        <CFormGroup variant="custom-checkbox" inline>
                                                                            <CInputCheckbox
                                                                                custom
                                                                                className="m-auto"
                                                                                checked={item.blockIfEmpty}
                                                                                name={`blockIfEmpty-${index}`}
                                                                                id={`blockIfEmpty-${index}`}
                                                                                onChange={(e) =>
                                                                                    setFieldValue(
                                                                                        `dataSources.${index}.blockIfEmpty`,
                                                                                        e.target.checked
                                                                                    )
                                                                                }
                                                                            />
                                                                            <CLabel htmlFor={`blockIfEmpty-${index}`} variant="custom-checkbox">
                                                                                Block analysis if no data
                                                                            </CLabel>
                                                                        </CFormGroup>

                                                                        <div className="position-relative">
                                                                            <HoverInfoIconTooltip
                                                                                iconName="iconInfoCircle"
                                                                                iconHeight={16}
                                                                                tooltipClassName="m-width-370"
                                                                            >
                                                                                <p className="whitespace-normal">
                                                                                    This report will be generated, and used as an input when the AI
                                                                                    Analysis is scheduled to run. If this report returns no data,
                                                                                    you can choose to block the entire analysis from running.
                                                                                </p>
                                                                            </HoverInfoIconTooltip>
                                                                        </div>
                                                                    </div>
                                                                </CCol>

                                                                <CCol lg="3">
                                                                    <div className="d-flex">
                                                                        {item.reportViewId && (
                                                                            <div
                                                                                className="d-flex align-items-center"
                                                                                onClick={() =>
                                                                                    handleEditField(
                                                                                        item.reportViewId,
                                                                                        item.descriptionOverride,
                                                                                        setFieldValue,
                                                                                        index
                                                                                    )
                                                                                }
                                                                            >
                                                                                <CButton
                                                                                    type="button"
                                                                                    color="link"
                                                                                    className="text-dark text-underline text-capitalize"
                                                                                >
                                                                                    Edit descriptions
                                                                                </CButton>

                                                                                <CLink className="option text-primary">
                                                                                    <CIcon icon="icon-pencil" height={18} />
                                                                                </CLink>
                                                                            </div>
                                                                        )}

                                                                        {values.dataSources.length > 1 && (
                                                                            <CButton
                                                                                className={`d-inline deleteicon ml-3 px-0 py-0`}
                                                                                onClick={() => arrayHelpers.remove(index)}
                                                                            >
                                                                                <CIcon icon="iconDeleteField" className="icon-delete" />
                                                                            </CButton>
                                                                        )}
                                                                    </div>
                                                                </CCol>
                                                            </CRow>
                                                        );
                                                    })}

                                                    <div className='d-flex items-center justify-content-between mb-4'>
                                                        <CButton
                                                            type="button"
                                                            onClick={() =>
                                                                arrayHelpers.push({
                                                                    id: Date.now(),
                                                                    reportViewId: '',
                                                                    blockIfEmpty: false,
                                                                    isNew: true,
                                                                })
                                                            }
                                                        >
                                                            <div className='d-flex align-items-center gap-2'>
                                                                <CIcon icon="iconAddField" width={24} height={24} />
                                                                <span>Add Data</span>
                                                            </div>
                                                        </CButton>
                                                    </div>
                                                </>
                                            )}
                                        />

                                        {typeof errors.dataSources === 'string' && (
                                            <div className="text-danger mb-3">{errors.dataSources}</div>
                                        )}

                                        <CButton disabled={isSubmitting || (loadingIndex !== null)} type="submit" className="px-4" color="primary">
                                            <Waiting isLoading={isSubmitting || (loadingIndex !== null)}>Save & Next</Waiting>
                                        </CButton>
                                    </CForm>
                                </>
                            )}
                        </>
                    );
                }}
            </Formik>
        </StepCardHeader>
    );
};

export default DataSources;
